import {createContext, useCallback, useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';
import {gitCommitSha} from '^utilities/dataSet';

export const AppContext = createContext(null);

export const AppContextProvider = ({children}) => {
    const [leftNavOpen, setLeftNavOpen] = useState(false);
    const [asideChildren, setAsideChildren] = useState(null);
    const [showScrollButtons, setShowScrollButtons] = useState(false);
    const newVersionToastId = useRef(null);
    const appStatusToastId = useRef(null);

    useEffect(() => {
        const appRefreshWorker = new Worker(new URL(
            './appRefresh.worker.js',
            import.meta.url,
        ));

        appRefreshWorker.addEventListener('message', () => {
            if (newVersionToastId.current) return;

            newVersionToastId.current = toast.warning(
                <div>
                    {'A new version of LabTracker is available. '}
                    <div
                        className={'d-inline text-decoration-underline'}
                        role={'button'}
                        onClick={() => window.location.reload()}
                    >
                        {'Refresh'}
                    </div>
                </div>,
                {
                    autoClose: false,
                    closeButton: false,
                    draggable: false,
                },
            );
        });

        appRefreshWorker.postMessage({
            gitCommitSha,
            origin: window.location.origin,
        });

        return () => {
            toast.dismiss(newVersionToastId.current);
            appRefreshWorker.terminate();
        };
    }, []);

    useEffect(() => {
        const appStatusWorker = new Worker(new URL(
            './appStatus.worker.js',
            import.meta.url,
        ));

        appStatusWorker.addEventListener('message', (event) => {
            if (event.data?.status === 'OK') {
                if (appStatusToastId.current) {
                    toast.dismiss(appStatusToastId.current);
                    appStatusToastId.current = null;
                }
                return;
            }

            if (appStatusToastId.current) return;

            appStatusToastId.current = toast.warning(
                <div>
                    {'LabTracker degradation/outage.  See '}
                    <a
                        href={event.data?.url}
                        target={'_blank'}
                        rel={'noreferrer'}
                    >
                        {'system status'}
                    </a>
                    {' for details.'}
                </div>,
                {
                    autoClose: false,
                },
            );
        });

        appStatusWorker.postMessage(null);

        return () => {
            toast.dismiss(appStatusToastId.current);
            appStatusWorker.terminate();
        };
    }, []);

    const setLeftNavOpenCallback = useCallback((val) => {
        setLeftNavOpen(val);

        if (val) {
            // Close the aside when opening the nav
            setAsideChildren(null);
        }
    }, []);

    const setAsideChildrenCallback = useCallback((val) => {
        setAsideChildren(val);

        if (val) {
            // Close the left nave when opening the aside
            setLeftNavOpen(false);
        }
    }, []);

    return <AppContext value={{
        leftNavOpen,
        setLeftNavOpen: setLeftNavOpenCallback,
        asideChildren,
        setAsideChildren: setAsideChildrenCallback,
        showScrollButtons,
        setShowScrollButtons,
    }}>
        {children}
    </AppContext>;
};
